import type { ShaderConstructor } from './base-shader';
import { ShaderCategory, type ShaderMetadata } from './shader-types';

// Effects
import { FireShader } from './effects/fire';
import { SlimeballShader } from './effects/slimeball';
import { FireballShader } from './effects/fireball';

// Objects
import { SpinningCubeShader } from './objects/spinningcube';

// Simulations
import { WaterWaveShader } from './simulations/waterwave';
import { SnowfallShader } from './simulations/snowfall';

/**
 * Registry of all available shaders organized by category
 */
export interface ShaderRegistryEntry {
  constructor: ShaderConstructor;
  metadata: ShaderMetadata;
}

export interface ShaderRegistry {
  [ShaderCategory.EFFECTS]: ShaderRegistryEntry[];
  [ShaderCategory.OBJECTS]: ShaderRegistryEntry[];
  [ShaderCategory.PATTERNS]: ShaderRegistryEntry[];
  [ShaderCategory.SIMULATIONS]: ShaderRegistryEntry[];
}

/**
 * Create a registry entry for a shader
 */
function createEntry(
  shaderConstructor: ShaderConstructor
): ShaderRegistryEntry {
  return {
    constructor: shaderConstructor,
    metadata: shaderConstructor.getMetadata(),
  };
}

/**
 * Complete registry of all available shaders
 */
export const SHADER_REGISTRY: ShaderRegistry = {
  [ShaderCategory.EFFECTS]: [
    createEntry(FireShader),
    createEntry(SlimeballShader),
    createEntry(FireballShader),
  ],
  [ShaderCategory.OBJECTS]: [createEntry(SpinningCubeShader)],
  [ShaderCategory.PATTERNS]: [
    // TODO: Add pattern shaders here
  ],
  [ShaderCategory.SIMULATIONS]: [
    createEntry(WaterWaveShader),
    createEntry(SnowfallShader),
  ],
};

/**
 * Get all shaders flattened into a single array
 */
export function getAllShaders(): ShaderRegistryEntry[] {
  return Object.values(SHADER_REGISTRY).flat();
}

/**
 * Get shaders by category
 */
export function getShadersByCategory(
  category: ShaderCategory
): ShaderRegistryEntry[] {
  return SHADER_REGISTRY[category] || [];
}

/**
 * Find a shader by name
 */
export function findShaderByName(
  name: string
): ShaderRegistryEntry | undefined {
  return getAllShaders().find((entry) => {
    const instance = new entry.constructor();
    return instance.name === name;
  });
}

/**
 * Get category display names
 */
export const CATEGORY_DISPLAY_NAMES: Record<ShaderCategory, string> = {
  [ShaderCategory.EFFECTS]: 'Effects',
  [ShaderCategory.OBJECTS]: 'Objects',
  [ShaderCategory.PATTERNS]: 'Patterns',
  [ShaderCategory.SIMULATIONS]: 'Simulations',
};
